import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdAccountDto } from '@/services/ws-crm/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Filter } from '@/js/libs/ui/src/V2';

import { useMarkedOptionLabel } from 'src/hooks/useMarkedOptionLabel';
import type { Option } from 'src/types';
import { useTaskTablesContext } from '../../context';

export const InvoicingAccountFilter: FC = () => {
  const [t] = useTranslation(['backend', 'common']);
  const filters = useTaskTablesContext('filters$');
  const { updateFilter, invoicingAccountPrefix, setInvoicingAccountPrefix, invoicingAccountRequest } = useTaskTablesContext();

  const options = useSubscribe(
    invoicingAccountRequest.data$,
    (response) => response?.data.items.map((item) => ({ label: item.name, value: item.id, active: item.active })) ?? [],
  );
  const total = useSubscribe(invoicingAccountRequest.data$, (response) => response?.data.total ?? 0);
  const loading = useSubscribe(invoicingAccountRequest.pending$);

  const handleChange = useCallbackRef((option: Option<IdAccountDto> | null) => {
    updateFilter('invoicingAccount', option);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: invoicingAccountPrefix, setFilter: setInvoicingAccountPrefix, total }),
    [invoicingAccountPrefix, setInvoicingAccountPrefix, total],
  );

  const formatOptionLabel = useMarkedOptionLabel({ t });

  return (
    <Filter<IdAccountDto>
      label={t('common:INVOICING_ACCOUNT')}
      options={options}
      selected={filters.invoicingAccount}
      onChange={handleChange}
      loading={loading}
      serverSideFiltering={serverSideFiltering}
      formatOptionLabel={formatOptionLabel}
      isClearable
      dataTestId='invoicing-account-filter'
    />
  );
};
